/**
 * VariationManagement (C-1c) — post-approval variations (↔ Change Control).
 * Route: /regulatory/variations
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from './Regulatory.module.css';
import {
  useVariationsQuery, useCreateVariation, VARIATION_STATUS_LABEL, VARIATION_TYPE_LABEL, type NewVariationInput,
} from './useRegulatory';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function statusClass(s: string) {
  if (s === 'APPROVED' || s === 'IMPLEMENTED') return styles.stageDone;
  if (s === 'REJECTED' || s === 'WITHDRAWN') return styles.stageWarn;
  return styles.stageActive;
}

export function VariationManagement() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const q = useVariationsQuery({ status: status || undefined, type: type || undefined });
  const create = useCreateVariation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewVariationInput>({ productName: '', description: '', variationType: 'TYPE_IB' });
  const [err, setErr] = useState<string | null>(null);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats;

  async function submit() {
    setErr(null);
    if (!form.productName.trim() || !form.description.trim()) { setErr('Product and description are required.'); return; }
    try {
      const created = await create.mutateAsync(form);
      setOpen(false);
      setForm({ productName: '', description: '', variationType: 'TYPE_IB' });
      navigate({ to: '/regulatory/variations/$id', params: { id: created.variationId } });
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Could not raise the variation.'); }
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Regulatory Affairs · RA-PLM"
        title="Variation Management"
        subtitle="Post-approval changes to registered products — classified by type, driven by Change Control, tracked to authority approval."
        actions={<button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setOpen(true); setErr(null); }}>Raise variation</button>}
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.in_progress}</span><span className={styles.kpiLabel}>In progress</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.under_review}</span><span className={styles.kpiLabel}>Under review</span></div>
          <div className={`${styles.kpi} ${stats.major_open > 0 ? styles.kpiWarn : ''}`}><span className={styles.kpiNum}>{stats.major_open}</span><span className={styles.kpiLabel}>Major (Type II) open</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.implemented}</span><span className={styles.kpiLabel}>Implemented</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} className={styles.select}>
          <option value="">All statuses</option>
          {Object.keys(VARIATION_STATUS_LABEL).map((s) => <option key={s} value={s}>{VARIATION_STATUS_LABEL[s]}</option>)}
        </select>
        <select aria-label="Type filter" value={type} onChange={(e) => setType(e.target.value)} className={styles.select}>
          <option value="">All types</option>
          <option value="TYPE_IA">Type IA</option><option value="TYPE_IB">Type IB</option><option value="TYPE_II">Type II</option><option value="NOTIFICATION">Notification</option>
        </select>
        <span className={styles.resultCount}>{items.length} variation{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading variations…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No variations match the current filters.</div>
      ) : (
        <table className={styles.table}>
          <thead><tr><th>Variation</th><th>Product</th><th>Type</th><th>Status</th><th>Change control</th><th>Submitted</th></tr></thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.variationId} className={styles.row} onClick={() => navigate({ to: '/regulatory/variations/$id', params: { id: v.variationId } })}>
                <td className={styles.mono}>{v.variationCode}</td>
                <td>{v.productName}</td>
                <td>{VARIATION_TYPE_LABEL[v.variationType]?.split(' (')[0] ?? v.variationType}</td>
                <td><span className={`${styles.pill} ${statusClass(v.status)}`}>{VARIATION_STATUS_LABEL[v.status] ?? v.status}</span></td>
                <td>{v.changeRequestId ? <span className={styles.sub}>{v.changeRequestTitle ?? 'linked'}</span> : '—'}</td>
                <td>{fmtDate(v.submissionDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label="Raise variation">
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Raise a variation</h2>
            {err && <div className={styles.err} role="alert">{err}</div>}
            <label className={styles.field}><span>Product *</span>
              <input type="text" value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })} placeholder="e.g. Ircan 100 mg/5 ml Injection" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Variation type</span>
                <select value={form.variationType} onChange={(e) => setForm({ ...form, variationType: e.target.value })}>
                  <option value="TYPE_IA">Type IA (minor, notify)</option>
                  <option value="TYPE_IB">Type IB (minor, tell-wait)</option>
                  <option value="TYPE_II">Type II (major, prior approval)</option>
                  <option value="NOTIFICATION">Notification</option>
                </select></label>
              <label className={styles.field}><span>Change request id (optional)</span>
                <input type="text" value={form.changeRequestId ?? ''} onChange={(e) => setForm({ ...form, changeRequestId: e.target.value })} placeholder="UUID of the driving CC" /></label>
            </div>
            <label className={styles.field}><span>Description *</span>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void submit()} disabled={create.isPending}>
                {create.isPending ? 'Raising…' : 'Raise variation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
